/**
 * Line icons, drawn inline.
 *
 * An icon set is a large dependency for the dozen glyphs this app uses, and
 * these inherit `currentColor` so one component works on the indigo sidebar and
 * on white. Every icon is decorative — the label beside it carries the meaning —
 * so they are all `aria-hidden`.
 */
import type { SVGProps } from 'react'

function Icon({ children, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const OverviewIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2.5" y="2.5" width="6" height="6" rx="1.2" />
    <rect x="11.5" y="2.5" width="6" height="6" rx="1.2" />
    <rect x="2.5" y="11.5" width="6" height="6" rx="1.2" />
    <rect x="11.5" y="11.5" width="6" height="6" rx="1.2" />
  </Icon>
)

export const UsersIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="7.5" cy="6.5" r="2.75" />
    <path d="M2.5 16.5c0-2.5 2.2-4.2 5-4.2s5 1.7 5 4.2" />
    <path d="M13 4.4a2.75 2.75 0 0 1 0 5.3M14.2 12.6c1.9.5 3.3 1.9 3.3 3.9" />
  </Icon>
)

export const ShieldIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M10 2.2 3.8 4.4v4.9c0 3.6 2.5 6.6 6.2 8.5 3.7-1.9 6.2-4.9 6.2-8.5V4.4Z" />
    <path d="m7.4 9.9 2 2 3.2-3.6" />
  </Icon>
)

export const LedgerIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M5 2.5h10a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z" />
    <path d="M7 6.5h6M7 10h6M7 13.5h3.5" />
  </Icon>
)

export const SyncIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M16.5 10a6.5 6.5 0 0 1-11.2 4.5" />
    <path d="M3.5 10a6.5 6.5 0 0 1 11.2-4.5" />
    <path d="M14.2 2.6v3h-3M5.8 17.4v-3h3" />
  </Icon>
)

export const AccountIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="7.5" />
    <circle cx="10" cy="8" r="2.6" />
    <path d="M5.2 16.1a5.4 5.4 0 0 1 9.6 0" />
  </Icon>
)

export const SignOutIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M12 14.5v1.5a1 1 0 0 1-1 1H4.5a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1H11a1 1 0 0 1 1 1V5" />
    <path d="M8.5 10h8M14 7.5 16.5 10 14 12.5" />
  </Icon>
)

export const MenuIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M3 5.5h14M3 10h14M3 14.5h14" />
  </Icon>
)

export const CloseIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M5 5l10 10M15 5 5 15" />
  </Icon>
)

export const CheckIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="m4 10.5 4 4 8-9" />
  </Icon>
)

export const CopyIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="7" y="7" width="10" height="10" rx="1.4" />
    <path d="M13 4.5a1 1 0 0 0-1-1H4.5a1 1 0 0 0-1 1V12a1 1 0 0 0 1 1" />
  </Icon>
)

export const LockIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="4" y="8.5" width="12" height="9" rx="1.4" />
    <path d="M7 8.5V6a3 3 0 0 1 6 0v2.5" />
  </Icon>
)

export const PlusIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M10 4v12M4 10h12" />
  </Icon>
)

export const ChevronIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="m7 4.5 5 5.5-5 5.5" />
  </Icon>
)

export const FlagIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M5 17V4h10l-2 3.5L15 11H5" />
  </Icon>
)

export const ForecastIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M3.5 15.5 8 9.5l3 3 4.5-6" />
    <path d="M12.5 6.5h3v3" />
  </Icon>
)

/**
 * Accounts receivable: an invoice with a clock, for money owed and ageing.
 * The two ideas together are what distinguishes it from the ledger glyph.
 */
export const ReceivableIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M11.5 3.5H5.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h5" />
    <path d="M7 7h4M7 10h2.5" />
    <circle cx="14" cy="13" r="3.5" />
    <path d="M14 11.5V13l1.2.9" />
  </Icon>
)

/**
 * Refunds: a coin turning back on itself. The arrow returns to where it came
 * from, which is what separates this from the sync glyph's closed circle —
 * money going out is a one-way movement, and money coming back is the point.
 */
export const RefundIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="10" cy="11" r="5.5" />
    <path d="M10 9v4M8.6 10.2h2.8" />
    <path d="M3.5 6.5h4v-4" />
    <path d="M3.9 6.1A7.5 7.5 0 0 1 15 4.6" />
  </Icon>
)

/**
 * Trust funding: a vault door with a level gauge falling below its mark.
 *
 * Deliberately not the shield used for roles, and not the ledger's book. What
 * this screen is about is a *level* against a threshold — money held that may
 * not be enough — so the glyph is a container with a line across it rather than
 * another document.
 */
export const TrustIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2.8" y="4" width="14.4" height="12" rx="1.6" />
    <circle cx="10" cy="10" r="3" />
    <path d="M10 7v1.4M10 13v-1.4" />
    <path d="M2.8 13.2h2M15.2 13.2h2" />
  </Icon>
)

/**
 * The executive dashboard: four panes read at once.
 *
 * A grid rather than another chart glyph, because what distinguishes this screen
 * from the forecast is not what it plots but that everything is on one page —
 * and the forecast already owns the rising line.
 */
export const ExecutiveIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="3" y="3.5" width="6" height="5.5" rx="1" />
    <rect x="11" y="3.5" width="6" height="5.5" rx="1" />
    <rect x="3" y="11" width="6" height="5.5" rx="1" />
    <rect x="11" y="11" width="6" height="5.5" rx="1" />
  </Icon>
)

/** A week on a calendar, for the weekly dashboard. */
export const WeekIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="3" y="4.5" width="14" height="12" rx="1.5" />
    <path d="M3 8.5h14M7 3v3M13 3v3M6 12h8" />
  </Icon>
)

/**
 * Direction arrows for a KPI tile.
 *
 * These carry meaning rather than decoration, which is the one place this file's
 * "every icon is decorative" rule does not hold: KAN-97 forbids conveying
 * favourable or unfavourable by colour alone, and on a greyscale print or under
 * a screen reader the arrow is what is left. So each is titled, and the tile
 * also prints the word — belt and braces, because a board pack screenshot has
 * neither colour fidelity nor a screen reader.
 */
function DirectionIcon({ title, children, ...rest }: SVGProps<SVGSVGElement> & { title: string }) {
  return (
    <svg
      role="img"
      aria-label={title}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="14"
      height="14"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const TrendUpIcon = (p: SVGProps<SVGSVGElement>) => (
  <DirectionIcon title="up" {...p}>
    <path d="M10 16V5" />
    <path d="m5.5 9.5 4.5-4.5 4.5 4.5" />
  </DirectionIcon>
)

export const TrendDownIcon = (p: SVGProps<SVGSVGElement>) => (
  <DirectionIcon title="down" {...p}>
    <path d="M10 4v11" />
    <path d="m5.5 10.5 4.5 4.5 4.5-4.5" />
  </DirectionIcon>
)

export const TrendFlatIcon = (p: SVGProps<SVGSVGElement>) => (
  <DirectionIcon title="unchanged" {...p}>
    <path d="M4 10h12" />
  </DirectionIcon>
)

/**
 * An unavailable figure. A struck-through circle rather than a warning
 * triangle: most of these tiles are unavailable because the module that
 * calculates them has not been built, which is a statement about the roadmap
 * and not a fault anybody needs to act on this morning.
 */
export const UnavailableIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="6.5" />
    <path d="m5.6 14.4 8.8-8.8" />
  </Icon>
)

/** Exception alerts. A bell, matching the notification centre's own icon. */
export const AlertBellIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M15 7.5a5 5 0 0 0-10 0c0 5-2 6.5-2 6.5h14s-2-1.5-2-6.5" />
    <path d="M11.4 16.5a1.7 1.7 0 0 1-2.8 0" />
  </Icon>
)

/**
 * Cash flow: banknote with an arrow leaving it.
 *
 * Deliberately not a dollar sign — half the glyphs on a finance screen could
 * carry one, and the sidebar has to be scannable by shape. The arrow is what
 * separates cash *flow* from a cash balance.
 */
export const CashIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <rect x="2.5" y="5.5" width="15" height="8" rx="1.5" />
    <circle cx="10" cy="9.5" r="2" />
    <path d="M6 17.5h8M11.5 16l2.5 1.5-2.5 1.5" />
  </Icon>
)

/**
 * Budget vs. actual: two bars of different heights against a baseline.
 *
 * The shape *is* the comparison, which is what makes it findable in a sidebar
 * beside a cash glyph that is also about money. A single bar would read as any
 * chart at all.
 */
export const BudgetIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <path d="M3 16.5h14" />
    <rect x="5" y="8" width="3.5" height="8.5" />
    <rect x="11.5" y="4.5" width="3.5" height="12" />
  </Icon>
)

/**
 * Legal production: a rising line over a clock face.
 *
 * Time and output together, which is what separates it from the plain
 * forecast chevron — this section is about hours turned into money, and the
 * sidebar has to be scannable by shape.
 */
export const ProductionIcon = (p: SVGProps<SVGSVGElement>) => (
  <Icon {...p}>
    <circle cx="7" cy="12" r="4.5" />
    <path d="M7 9.5V12l1.75 1" />
    <path d="M12.5 9 15 6.5M15 6.5h-2.5M15 6.5V9" />
    <path d="M12 16.5h5.5" />
  </Icon>
)
